// src/components/admin/PublicServiceManager.js
//
// Admin tool for managing public-service podcast content (DR and similar
// state-funded broadcasters). Podcasts imported here are:
//   - Freely catalogued without creator verification
//   - Tagged contentType: 'public_service' in Firestore
//   - Excluded from the 80% creator payment calculation
//   - Still tracked in user listening history

import { useState, useEffect, useCallback } from 'react';
import { Radio, RefreshCw, Plus, AlertCircle, CheckCircle, Loader, ChevronDown, ChevronUp, Clock } from 'lucide-react';
import { db } from '../../firebase';
import { doc, getDoc } from 'firebase/firestore';
import {
  importPublicServicePodcast,
  syncPublicServicePodcast,
  syncAllPublicServicePodcasts,
  getPublicServicePodcasts,
  discoverAndImportDRCatalogue,
} from '../../services/PublicServiceImportService';

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatDate(val) {
  if (!val) return '—';
  const d = val.toDate ? val.toDate() : new Date(val);
  return d.toLocaleDateString('da-DK', { day: 'numeric', month: 'short', year: 'numeric' });
}

function StatusBadge({ text, type }) {
  const colors = {
    success: 'bg-emerald-900/40 text-emerald-400 border-emerald-700/40',
    error:   'bg-red-900/40 text-red-400 border-red-700/40',
    info:    'bg-blue-900/40 text-blue-400 border-blue-700/40',
  };
  return (
    <div className={`flex items-start gap-2 p-3 rounded-lg border text-sm ${colors[type]}`}>
      {type === 'success' && <CheckCircle className="w-4 h-4 mt-0.5 shrink-0" />}
      {type === 'error'   && <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />}
      {type === 'info'    && <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />}
      <span>{text}</span>
    </div>
  );
}

// ─── Component ───────────────────────────────────────────────────────────────

export default function PublicServiceManager() {
  const [podcasts, setPodcasts]             = useState([]);
  const [loading, setLoading]               = useState(true);
  const [feedUrl, setFeedUrl]               = useState('');
  const [provider, setProvider]             = useState('dr');
  const [importing, setImporting]           = useState(false);
  const [importStatus, setImportStatus]     = useState(null); // { text, type }
  const [syncingAll, setSyncingAll]         = useState(false);
  const [syncAllStatus, setSyncAllStatus]   = useState(null);
  const [discovering, setDiscovering]       = useState(null); // 'dr' | null
  const [discoverStatus, setDiscoverStatus] = useState(null);
  const [discoverProgress, setDiscoverProgress] = useState(null);
  const [syncingId, setSyncingId]           = useState(null);
  const [expandedId, setExpandedId]         = useState(null);
  const [syncHealth, setSyncHealth]         = useState(null); // last GitHub Actions run status

  const loadSyncHealth = useCallback(async () => {
    try {
      const snap = await getDoc(doc(db, 'system_status', 'dr_sync'));
      if (snap.exists()) setSyncHealth(snap.data());
    } catch { /* non-critical — ignore */ }
  }, []);

  const loadPodcasts = useCallback(async () => {
    try {
      const list = await getPublicServicePodcasts();
      list.sort((a, b) => (a.title || '').localeCompare(b.title || '', 'da'));
      setPodcasts(list);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadPodcasts(); loadSyncHealth(); }, [loadPodcasts, loadSyncHealth]);

  // ── Manual import ───────────────────────────────────────────────────────────

  async function handleImport(e) {
    e.preventDefault();
    if (!feedUrl.trim()) return;
    setImporting(true);
    setImportStatus(null);
    const result = await importPublicServicePodcast(feedUrl.trim(), provider);
    setImportStatus({
      text: result.message,
      type: result.success ? 'success' : 'error',
    });
    if (result.success) {
      setFeedUrl('');
      await loadPodcasts();
    }
    setImporting(false);
  }

  // ── Sync single podcast ──────────────────────────────────────────────────────

  async function handleSync(podcast) {
    setSyncingId(podcast.id);
    const result = await syncPublicServicePodcast(podcast.id, podcast.feedUrl);
    // Refresh list to pick up updated lastSyncedAt / episodeCount
    await loadPodcasts();
    setSyncingId(null);
    // Briefly show result on expanded row
    setExpandedId(podcast.id);
    setTimeout(() => setExpandedId(null), 3000);
    return result;
  }

  // ── Sync all ─────────────────────────────────────────────────────────────────

  async function handleSyncAll() {
    setSyncingAll(true);
    setSyncAllStatus(null);
    const result = await syncAllPublicServicePodcasts();
    if (result.success) {
      setSyncAllStatus({
        text: `Synkroniserede ${result.synced} podcasts · ${result.newEpisodes} nye episoder tilføjet`,
        type: 'success',
      });
      await loadPodcasts();
    } else {
      setSyncAllStatus({ text: result.message, type: 'error' });
    }
    setSyncingAll(false);
  }

  // ── DR auto-discover ─────────────────────────────────────────────────────────

  async function handleDRDiscover() {
    setDiscovering('dr');
    setDiscoverStatus(null);
    setDiscoverProgress(null);
    const result = await discoverAndImportDRCatalogue((progress) => {
      setDiscoverProgress(progress);
    });
    if (result.success) {
      setDiscoverStatus({
        text: `DR-katalog: ${result.imported} nye · ${result.updated} opdateret · ${result.failed} fejlede`,
        type: result.failed > 0 ? 'info' : 'success',
      });
      await loadPodcasts();
    } else {
      setDiscoverStatus({
        text: `Kunne ikke hente DR-katalog: ${result.message}. Brug manuel import i stedet.`,
        type: 'error',
      });
    }
    setDiscoverProgress(null);
    setDiscovering(null);
  }

  // ─── Render ──────────────────────────────────────────────────────────────────

  // ── Sync health banner ───────────────────────────────────────────────────────

  function SyncHealthBanner() {
    if (!syncHealth) return null;

    const lastRun  = syncHealth.lastRunAt?.toDate ? syncHealth.lastRunAt.toDate() : null;
    const ageHours = lastRun ? (Date.now() - lastRun.getTime()) / 36e5 : null;
    const isStale  = ageHours !== null && ageHours > 25;

    const status = isStale ? 'stale' : syncHealth.status;

    const cfg = {
      success: {
        bg:   'bg-emerald-900/30 border-emerald-700/40',
        icon: <CheckCircle className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />,
        text: `Seneste sync: ${lastRun?.toLocaleString('da-DK') ?? '—'} · ${syncHealth.imported} importeret · ${syncHealth.updated} opdateret`,
        sub:  null,
      },
      partial: {
        bg:   'bg-amber-900/30 border-amber-700/40',
        icon: <AlertCircle className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />,
        text: `Seneste sync: ${lastRun?.toLocaleString('da-DK') ?? '—'} · ${syncHealth.failed} feeds fejlede`,
        sub:  syncHealth.failedFeeds?.length
          ? syncHealth.failedFeeds.map(f => f.title).join(', ')
          : null,
      },
      failed: {
        bg:   'bg-red-900/30 border-red-700/40',
        icon: <AlertCircle className="w-4 h-4 text-red-400 shrink-0 mt-0.5" />,
        text: `Seneste sync mislykkedes (${lastRun?.toLocaleString('da-DK') ?? 'ukendt tidspunkt'})`,
        sub:  syncHealth.error || null,
      },
      stale: {
        bg:   'bg-red-900/30 border-red-700/40',
        icon: <Clock className="w-4 h-4 text-red-400 shrink-0 mt-0.5" />,
        text: `Ingen sync i ${Math.round(ageHours)} timer — GitHub Actions ser ud til ikke at køre`,
        sub:  'Tjek Actions-fanen på GitHub for fejl.',
      },
    };

    const c = cfg[status] || cfg.success;
    return (
      <div className={`flex items-start gap-3 p-4 rounded-xl border text-sm ${c.bg}`}>
        {c.icon}
        <div>
          <p className="text-slate-200">{c.text}</p>
          {c.sub && <p className="text-slate-400 mt-0.5 text-xs">{c.sub}</p>}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">

      {/* Sync health banner */}
      <SyncHealthBanner />

      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-white flex items-center gap-3">
          <Radio className="w-6 h-6 text-blue-400" />
          Public service-indhold
        </h2>
        <p className="text-slate-400 mt-1 text-sm">
          Podcasts importeret herfra er gratis tilgængeligt indhold fra statsfinansierede medier.
          De indgår <strong className="text-slate-300">ikke</strong> i skaberbetalingsberegningen.
        </p>
      </div>

      {/* Manual import */}
      <div className="bg-slate-800/50 rounded-xl border border-slate-700/30 p-6">
        <h3 className="text-lg font-semibold text-white mb-4">Importer podcast via RSS</h3>
        <form onSubmit={handleImport} className="flex flex-col gap-3">
          <div className="flex gap-3">
            <input
              type="url"
              value={feedUrl}
              onChange={e => setFeedUrl(e.target.value)}
              placeholder="https://www.dr.dk/mu/feed/..."
              required
              className="flex-1 bg-slate-900 border border-slate-600 rounded-lg px-4 py-2.5 text-white placeholder-slate-500 text-sm focus:outline-none focus:border-blue-500"
            />
            <select
              value={provider}
              onChange={e => setProvider(e.target.value)}
              className="bg-slate-900 border border-slate-600 rounded-lg px-3 py-2.5 text-white text-sm focus:outline-none focus:border-blue-500"
            >
              <option value="dr">DR</option>
              <option value="other">Andet</option>
            </select>
            <button
              type="submit"
              disabled={importing || !feedUrl.trim()}
              className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed px-4 py-2.5 rounded-lg text-white text-sm font-medium transition-colors"
            >
              {importing
                ? <Loader className="w-4 h-4 animate-spin" />
                : <Plus className="w-4 h-4" />}
              Importer
            </button>
          </div>
          {importStatus && <StatusBadge {...importStatus} />}
        </form>
      </div>

      {/* DR auto-discover */}
      <div className="bg-slate-800/50 rounded-xl border border-slate-700/30 p-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="text-lg font-semibold text-white">Opdater DR-katalog automatisk</h3>
            <p className="text-slate-400 text-sm mt-1">
              Henter hele DR's podcast-katalog via DR MU API og importerer nye podcasts.
              Eksisterende podcasts får kun nye episoder — ingen dubletter.
              <br />
              <span className="text-slate-500">
                Bemærk: Dette kræver at DR's API tillader browser-forespørgsler (CORS).
                Hvis det fejler, brug den automatiske GitHub Actions-opdatering i stedet.
              </span>
            </p>
          </div>
          <button
            onClick={handleDRDiscover}
            disabled={!!discovering}
            className="flex items-center gap-2 bg-slate-700 hover:bg-slate-600 disabled:opacity-50 disabled:cursor-not-allowed px-4 py-2.5 rounded-lg text-white text-sm font-medium transition-colors whitespace-nowrap"
          >
            {discovering === 'dr'
              ? <Loader className="w-4 h-4 animate-spin" />
              : <Radio className="w-4 h-4" />}
            Hent DR-katalog
          </button>
        </div>
        {discoverProgress && (
          <div className="mt-3">
            <div className="flex justify-between text-xs text-slate-400 mb-1">
              <span>Importerer…</span>
              <span>{discoverProgress.done} / {discoverProgress.total}</span>
            </div>
            <div className="w-full bg-slate-700 rounded-full h-1.5">
              <div
                className="bg-blue-500 h-1.5 rounded-full transition-all duration-300"
                style={{ width: `${discoverProgress.total ? (discoverProgress.done / discoverProgress.total) * 100 : 0}%` }}
              />
            </div>
          </div>
        )}
        {discoverStatus && <div className="mt-3"><StatusBadge {...discoverStatus} /></div>}
      </div>

      {/* Sync all + count */}
      <div className="flex items-center justify-between">
        <p className="text-slate-400 text-sm">
          {loading ? 'Indlæser…' : `${podcasts.length} public service-podcasts i kataloget`}
        </p>
        <button
          onClick={handleSyncAll}
          disabled={syncingAll || podcasts.length === 0}
          className="flex items-center gap-2 bg-slate-700 hover:bg-slate-600 disabled:opacity-50 disabled:cursor-not-allowed px-4 py-2 rounded-lg text-white text-sm font-medium transition-colors"
        >
          {syncingAll
            ? <Loader className="w-4 h-4 animate-spin" />
            : <RefreshCw className="w-4 h-4" />}
          Synkroniser alle
        </button>
      </div>
      {syncAllStatus && <StatusBadge {...syncAllStatus} />}

      {/* Podcast list */}
      {loading ? (
        <div className="flex items-center gap-3 text-slate-400 py-8">
          <Loader className="w-5 h-5 animate-spin" />
          Indlæser podcasts…
        </div>
      ) : podcasts.length === 0 ? (
        <div className="bg-slate-800/30 rounded-xl border border-slate-700/20 p-8 text-center">
          <Radio className="w-10 h-10 text-slate-600 mx-auto mb-3" />
          <p className="text-slate-400">Ingen public service-podcasts importeret endnu.</p>
          <p className="text-slate-500 text-sm mt-1">Brug formularen ovenfor til at komme i gang.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {podcasts.map(podcast => (
            <div
              key={podcast.id}
              className="bg-slate-800/40 rounded-xl border border-slate-700/30 overflow-hidden"
            >
              {/* Row */}
              <div className="flex items-center gap-4 p-4">
                {/* Artwork */}
                <div className="w-12 h-12 rounded-lg bg-slate-700 overflow-hidden shrink-0">
                  {podcast.image
                    ? <img src={podcast.image} alt="" className="w-full h-full object-cover" />
                    : <div className="w-full h-full flex items-center justify-center text-slate-500 text-xl">📻</div>
                  }
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-white truncate">{podcast.title}</p>
                  <p className="text-slate-400 text-xs mt-0.5">
                    {podcast.episodeCount ?? '?'} episoder
                    {' · '}
                    Sidst synkroniseret: {formatDate(podcast.lastSyncedAt)}
                    {' · '}
                    <span className="uppercase tracking-wide text-slate-500">
                      {podcast.publicServiceProvider || 'public service'}
                    </span>
                  </p>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2 shrink-0">
                  <button
                    onClick={() => handleSync(podcast)}
                    disabled={syncingId === podcast.id}
                    title="Synkroniser episoder nu"
                    className="p-2 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg transition-colors disabled:opacity-50"
                  >
                    {syncingId === podcast.id
                      ? <Loader className="w-4 h-4 animate-spin" />
                      : <RefreshCw className="w-4 h-4" />}
                  </button>
                  <button
                    onClick={() => setExpandedId(expandedId === podcast.id ? null : podcast.id)}
                    className="p-2 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg transition-colors"
                  >
                    {expandedId === podcast.id
                      ? <ChevronUp className="w-4 h-4" />
                      : <ChevronDown className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              {/* Expanded detail */}
              {expandedId === podcast.id && (
                <div className="border-t border-slate-700/30 px-4 py-3 bg-slate-900/30">
                  <p className="text-slate-400 text-sm line-clamp-3 mb-2">
                    {podcast.description || 'Ingen beskrivelse'}
                  </p>
                  <p className="text-slate-500 text-xs font-mono break-all">{podcast.feedUrl}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
